import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";

import { CategoryPicker } from "@/components/portal/lms/CategoryPicker";
import { CuratorGate } from "@/components/portal/lms/CuratorGate";
import { GenerateBar } from "@/components/portal/lms/GenerateBar";
import { QuestionList } from "@/components/portal/lms/QuestionList";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { useAuth } from "@/contexts/AuthContext";
import { useLmsCuratorAccess } from "@/hooks/useLmsCuratorAccess";
import type { CategoryHierarchyNode } from "@/services/api/categories";
import { PageHeader } from "@/components/ui/page-header";

export const Route = createFileRoute("/portal/lms/curation")({
  component: LmsCurationPage,
});

/**
 * LMS question-curation page (`/portal/lms/curation`). Pick a RAG category
 * the caller curates, review its AI-drafted questions, and Approve / Edit /
 * Delete / Generate. The picker lists only categories `useLmsCuratorAccess`
 * admits; the real per-category authorisation is dp lms's
 * require_category_curator — this page just surfaces what the API returns.
 */
function LmsCurationPage() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const queryClient = useQueryClient();

  const orgId = selectedOrganisation?.organisationId ?? "";
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const access = useLmsCuratorAccess();
  const { canCurate } = access;
  const curatorFilter = useCallback(
    (node: CategoryHierarchyNode) => canCurate(node.id),
    [canCurate],
  );

  return (
    <div className="space-y-4" data-testid="lms-curator-page">
      <Helmet>
        <title>Question Curation | LMS</title>
      </Helmet>
      <PageHeader
        title="Question curation"
        description="Review AI-drafted questions for a category, then approve, edit or delete each — and generate new batches."
      />

      <CuratorGate access={access}>
        <CategoryPicker
          token={token ?? ""}
          orgId={orgId}
          value={categoryId}
          onChange={setCategoryId}
          filter={curatorFilter}
          emptyMessage="You don't curate any categories."
          placeholder="Select a category to curate"
        />

        {categoryId ? (
          // key={categoryId} remounts both on a category switch: it clears any
          // in-flight generate job + edit/error state so a run started on one
          // category can't complete into (or invalidate) another's cache.
          <div className="space-y-4">
            <GenerateBar
              key={categoryId}
              token={token ?? ""}
              categoryId={categoryId}
              onComplete={() =>
                queryClient.invalidateQueries({
                  queryKey: ["lms", "questions", categoryId],
                })
              }
            />
            <QuestionList
              key={categoryId}
              token={token ?? ""}
              categoryId={categoryId}
            />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Select a category to review its draft questions.
          </p>
        )}
      </CuratorGate>
    </div>
  );
}
